import { useCallback } from 'react';
import styled from 'styled-components';

import { AttachedFile } from '@hivebrite/design-system/AttachedFile';
import { Box } from '@hivebrite/design-system/Box';

import { isInLoadingState } from 'hb-react/shared/hooks/useGlobalStorageV2/utils';
import useVirusScanningEnabled from 'hb-react/shared/hooks/useVirusScanningEnabled';

import { useQuillEditorContext } from '../contexts/QuillEditorContext';

const Attachment = ({ attachment, onRemoveAttachment, size }) => {
  const virusScanningEnabled = useVirusScanningEnabled();

  const handleRemoveAttachment = useCallback(
    () => onRemoveAttachment(attachment),
    [onRemoveAttachment, attachment],
  );

  // HACK: While we support QuilEditor V1 and V2/3, ignore the virus scan status
  // if attachment is not a GlobalStorageFile (i.e. has a loading property OR no key property)
  const keys = Object.keys(attachment);
  const isLoading =
    keys.includes('loading') || !keys.includes('key')
      ? attachment.loading
      : isInLoadingState(attachment, virusScanningEnabled);

  return (
    <AttachedFile
      file={attachment.file ?? attachment.fileMetadata}
      loading={isLoading}
      onRemove={handleRemoveAttachment}
      error={attachment.error}
      size={size}
    />
  );
};

const Attachments = () => {
  const { onRemoveFile, attachments, compact } = useQuillEditorContext();

  if (!attachments?.length) {
    return null;
  }

  return (
    <Wrapper>
      {attachments.map((attachment) => (
        <Attachment
          key={attachment.key || attachment.id}
          attachment={attachment}
          onRemoveAttachment={onRemoveFile}
          size={compact ? 'small' : 'normal'}
        />
      ))}
    </Wrapper>
  );
};

export default Attachments;

const Wrapper = styled(Box).attrs(() => ({
  px: 3,
  mb: 6,
  flexDirection: 'column',
  display: 'block',
}))`
  > * + * {
    margin-top: ${(props) => props.theme.space[3]}px;
  }
`;
